import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/App";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CreditCard, CheckCircle, Clock, AlertTriangle, Loader2 } from "lucide-react";

function SubscriptionBadge({ isActive, isTrial }) {
  if (isActive) {
    return <Badge className="rounded-sm text-sm px-3 py-1 bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle className="h-4 w-4 mr-1" /> Attivo</Badge>;
  }
  if (isTrial) {
    return <Badge className="rounded-sm text-sm px-3 py-1 bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="h-4 w-4 mr-1" /> Periodo di Prova</Badge>;
  }
  return <Badge className="rounded-sm text-sm px-3 py-1 bg-red-100 text-red-800 hover:bg-red-100"><AlertTriangle className="h-4 w-4 mr-1" /> Scaduto</Badge>;
}

export default function SubscriptionPage() {
  const { user, checkAuth } = useAuth();
  const [searchParams] = useSearchParams();
  const [settings, setSettings] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [paying, setPaying] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");

  useEffect(() => {
    const fetchData = async () => {
      try {
        const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
        // Use plain fetch (no credentials) for public pricing endpoint to avoid CORS issues
        const pricingRes = await fetch(`${API}/payments/prices`).catch(() => null);
        const s = pricingRes && pricingRes.ok ? await pricingRes.json() : null;
        const h = await api.get("/payments/history").catch(() => []);
        setSettings(s || { subscription_price: 99, subscription_currency: "eur", price_1_month: 12, price_3_months: 30, price_6_months: 55, price_9_months: 75 });
        setHistory(h);
      } catch (e) {
        console.error("Failed to load settings/history:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Poll payment status on return from Stripe
  useEffect(() => {
    if (!sessionId) return;
    setPolling(true);
    let attempts = 0;
    const maxAttempts = 8;
    const pollInterval = 2500;

    const poll = async () => {
      if (attempts >= maxAttempts) {
        setPolling(false);
        toast.error("Verifica pagamento scaduta. Controlla la tua email.");
        return;
      }
      try {
        const status = await api.get(`/payments/status/${sessionId}`);
        if (status.payment_status === "paid") {
          setPolling(false);
          toast.success("Pagamento completato! Abbonamento attivato.");
          await checkAuth();
          const h = await api.get("/payments/history").catch(() => []);
          setHistory(h);
          return;
        }
        attempts++;
        setTimeout(poll, pollInterval);
      } catch (e) {
        attempts++;
        setTimeout(poll, pollInterval);
      }
    };
    poll();
  }, [sessionId, checkAuth]);

  useEffect(() => {
    if (cancelled) {
      toast.error("Pagamento annullato");
    }
  }, [cancelled]);

  const handleCheckout = async (method) => {
    if (!selectedPlan) {
      toast.error("Seleziona un piano");
      return;
    }
    const plan = plans.find(p => p.key === selectedPlan);
    if (!plan) return;
    setPaying(true);
    try {
      const origin = window.location.origin;
      const data = await api.post("/payments/checkout", { origin_url: origin, plan_key: selectedPlan, months: plan.months, amount: plan.price });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      toast.error(e?.message || "Errore nell'avvio del pagamento");
      setPaying(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-green-700" /></div>;
  }

  const sub = user?.subscription || {};
  const isActive = sub.status === "active";
  const isTrial = sub.status === "trial";
  const isExpired = !isActive && !isTrial;

  const endDate = sub.subscription_end || sub.trial_end;
  const endDateFormatted = endDate ? new Date(endDate).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" }) : "-";

  const daysLeft = endDate ? Math.max(0, Math.ceil((new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  // Inferisce il nome del piano dalla durata quando plan_name non è salvato nel DB
  const PLAN_DAYS_MAP = [[1,30],[3,90],[6,180],[9,270],[12,365]];
  const PLAN_LABELS_MAP = {1:"1 Mese",3:"3 Mesi",6:"6 Mesi",9:"9 Mesi",12:"Annuale"};
  const inferredPlanName = (() => {
    if (sub.plan_name) return sub.plan_name;
    if (!isActive || !sub.subscription_end) return null;
    const totalDays = Math.round((new Date(sub.subscription_end) - new Date()) / (1000 * 60 * 60 * 24));
    const closest = PLAN_DAYS_MAP.reduce((a, b) => Math.abs(b[1] - totalDays) < Math.abs(a[1] - totalDays) ? b : a);
    return PLAN_LABELS_MAP[closest[0]] || `${closest[0]} Mesi`;
  })();

  const plans = settings ? [
    { key: "1m", label: "1 Mese", months: 1, price: settings.price_1_month || 12 },
    { key: "3m", label: "3 Mesi", months: 3, price: settings.price_3_months || 30 },
    { key: "6m", label: "6 Mesi", months: 6, price: settings.price_6_months || 55 },
    { key: "9m", label: "9 Mesi", months: 9, price: settings.price_9_months || 75 },
    { key: "12m", label: "12 Mesi", months: 12, price: settings.subscription_price || 99, popular: true },
  ] : [];

  const currency = (settings?.subscription_currency || "eur").toUpperCase();

  return (
    <div className="space-y-8 max-w-4xl mx-auto" data-testid="subscription-page">
      <div>
        <p className="text-green-700 text-xs font-bold uppercase tracking-widest mb-1">Abbonamento</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">Il Tuo Piano</h1>
      </div>

      {polling && (
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="py-4 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-amber-700" />
            <p className="text-amber-800 font-medium">Verifica pagamento in corso...</p>
          </CardContent>
        </Card>
      )}

      {/* Current Plan */}
      <Card className="bg-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="font-heading text-lg font-bold uppercase">Stato Abbonamento</CardTitle>
            <SubscriptionBadge isActive={isActive} isTrial={isTrial} />
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide font-bold">Piano</p>
              <p className="text-lg font-medium">{isActive ? (inferredPlanName || "Abbonamento") : isTrial ? "Prova Gratuita" : "Nessuno"}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide font-bold">Scadenza</p>
              <p className="text-lg font-medium">{endDateFormatted}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-wide font-bold">Giorni Rimanenti</p>
              <p className={`text-lg font-bold ${daysLeft <= 7 ? "text-red-600" : daysLeft <= 30 ? "text-amber-600" : "text-green-700"}`}>
                {daysLeft}
              </p>
            </div>
          </div>
          {isTrial && (
            <div className="bg-amber-50 rounded-sm p-4 border border-amber-200">
              <p className="text-sm text-amber-800">
                Stai usando il periodo di prova gratuito. Puoi creare fino a <strong>{settings?.trial_max_categories || 4} categorie</strong> e <strong>{settings?.trial_max_players_per_category || 32} giocatori per categoria</strong> per torneo.
                Per limiti illimitati, effettua l'upgrade!
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plans */}
      {(!isActive || daysLeft <= 30) && plans.length > 0 && (
        <Card className="bg-white border-2 border-green-200">
          <CardHeader>
            <CardTitle className="font-heading text-lg font-bold uppercase">
              {isActive ? "Rinnova Abbonamento" : "Scegli il Tuo Piano"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {plans.map(plan => (
                <div
                  key={plan.key}
                  data-testid={`plan-${plan.key}`}
                  onClick={() => setSelectedPlan(plan.key)}
                  className={`relative border-2 rounded-sm p-4 text-center cursor-pointer transition-all ${
                    selectedPlan === plan.key
                      ? "border-green-600 bg-green-50 shadow-md"
                      : plan.popular
                        ? "border-purple-300 bg-purple-50 hover:border-purple-400"
                        : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 bg-purple-700 text-white text-[9px] font-bold uppercase px-2 py-0.5 rounded-sm tracking-wide">Consigliato</span>
                  )}
                  <p className="text-xs text-slate-500 font-medium mt-1">{plan.label}</p>
                  <p className="text-2xl font-heading font-black text-slate-900 mt-1">{plan.price.toFixed(2)}</p>
                  <p className="text-[10px] text-slate-400">{currency}</p>
                  {plan.months > 1 && (
                    <p className="text-[10px] text-slate-400 mt-1">{(plan.price / plan.months).toFixed(2)} {currency}/mese</p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="pay-stripe-btn"
                onClick={() => handleCheckout("stripe")}
                disabled={paying || !selectedPlan}
                className="flex-1 bg-green-700 hover:bg-green-800 text-white font-bold uppercase tracking-wide rounded-sm gap-2 py-5 disabled:opacity-50"
              >
                {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
                {selectedPlan ? `Paga ${plans.find(p => p.key === selectedPlan)?.price.toFixed(2)} ${currency}` : "Seleziona un piano"}
              </Button>
            </div>
            <p className="text-[10px] text-slate-400 text-center">Categorie illimitate &middot; Tutte le funzionalita &middot; Pagamento sicuro tramite Stripe</p>
          </CardContent>
        </Card>
      )}

      {/* Payment History */}
      {history.length > 0 && (
        <Card className="bg-white">
          <CardHeader><CardTitle className="font-heading text-lg font-bold uppercase">Storico Pagamenti</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase">Data</TableHead>
                  <TableHead className="text-xs uppercase">Importo</TableHead>
                  <TableHead className="text-xs uppercase">Stato</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(txn => (
                  <TableRow key={txn.transaction_id || txn.session_id}>
                    <TableCell className="text-sm">{txn.created_at?.split("T")[0]}</TableCell>
                    <TableCell className="font-medium">{txn.amount?.toFixed(2)} {(txn.currency || "eur").toUpperCase()}</TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm text-xs ${
                        txn.payment_status === "paid" ? "bg-green-100 text-green-800 hover:bg-green-100" :
                        "bg-slate-100 text-slate-600 hover:bg-slate-100"
                      }`}>
                        {txn.payment_status === "paid" ? "Pagato" : txn.payment_status}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
